import type {
  FreeResult,
  InterpretationTrap,
  PerspectiveReportContent,
  WFDimensionDeepDive,
  WFMisfitSignal,
  WorkFitReportContent,
} from '../../lib/apiClient';
import { PERSPECTIVE_PATTERN_LABELS, WF_DIMENSION_LABELS } from '../../lib/assessment/content';
import {
  PS_HIDDEN_DEMAND,
  PS_OLD_LENS,
  PS_OPERATING_STATEMENTS,
  PS_WATCH_FOR_NOTES,
} from '../../lib/report/perspectiveReportContent';
import {
  PSP_CONSCIOUS_VS_SUBCONSCIOUS,
  PSP_LABEL_TO_SLUG,
  PSP_PRIMARY_TRAP_BY_SLUG,
  PSP_DOMINANT_INTERACTION_NOTE,
  PSP_MINDSET_FRAMING,
  PSP_SECONDARY_AS_MODIFIER,
  PSP_SEVEN_DAY_NARRATIVE,
  PSP_THIRTY_DAY_WEEKS,
  PSP_TRAP_PAGE_INTRO,
} from '../../lib/report/perspectivePrintContent';
import { PrintCard, PrintLabel, PrintPage } from './printPages';

/**
 * The Work Fit and Perspective printed documents, copied from production's
 * `work-fit-report-print.tsx` and `perspective-report-print.tsx`.
 *
 * Production paginates each into a cover plus one titled page per topic, and
 * **gives each environment need a page of its own** in the Work Fit document —
 * the page title is the dimension's label. V2 printed the screen report.
 */
function Cover({
  eyebrow,
  primaryType,
  lead,
  preparedFor,
  completedAt,
  assessmentLine,
}: {
  eyebrow: string;
  primaryType: string;
  lead?: string;
  preparedFor: string | null;
  completedAt: string | null;
  /** Production names the assessment on the cover meta block. */
  assessmentLine?: string;
}) {
  const completedDate = completedAt
    ? new Date(completedAt).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <PrintPage variant="cover" testId="print-cover">
      <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.22em] text-primary">
        {eyebrow}
      </p>
      <p className="text-3xl font-extrabold leading-tight">{primaryType}</p>
      {lead ? (
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">{lead}</p>
      ) : null}

      <hr className="my-8 border-border" />

      <dl className="flex flex-col gap-1 text-sm">
        {preparedFor ? (
          <div className="flex gap-2">
            <dt className="font-bold">Prepared for</dt>
            <dd data-testid="print-prepared-for">{preparedFor}</dd>
          </div>
        ) : null}
        {completedDate ? (
          <div className="flex gap-2">
            <dt className="font-bold">Completed</dt>
            <dd>{completedDate}</dd>
          </div>
        ) : null}
        {assessmentLine ? (
          <div className="flex gap-2">
            <dt className="font-bold">Assessment</dt>
            <dd data-testid="print-assessment-line">{assessmentLine}</dd>
          </div>
        ) : null}
        <div className="flex gap-2">
          <dt className="font-bold">Report Type</dt>
          <dd>Full Paid Report</dd>
        </div>
      </dl>

      <p className="mt-10 text-xs leading-relaxed text-muted-foreground">
        This assessment is for personal development and coaching purposes only. It is not medical,
        psychological, or clinical advice. The insights and recommendations in this report are
        designed to support professional performance and personal growth.
      </p>
    </PrintPage>
  );
}

/** Production's three closing steps on the Work Fit print document. */
const WF_FINAL_STEPS = [
  {
    title: 'Bring the question bank to every interview.',
    body: 'These questions are calibrated to your specific profile. They surface information that generic interview prep misses.',
  },
  {
    title: 'Share the Environment Needs section with your manager.',
    body: 'This is a practical tool for improving your current role, not just evaluating new ones.',
  },
  {
    title: 'Return to High Performer OS to complete the full bottleneck assessment.',
    body: 'Work Fit tells you what environment you need. The bottleneck assessment tells you what is holding you back inside it.',
  },
];

/*
  Keys rather than an index: the no-browser-database rule scans for `.from(`
  and cannot tell `Array.from` from a Supabase query builder.
*/
const LINE_KEYS = ['a', 'b', 'c'] as const;

/** Ruled lines to write on, where the printed document leaves room for notes. */
function WriteLines({ count = 2 }: { count?: number }) {
  return (
    <div className="mt-1 flex flex-col" aria-hidden="true" data-testid="print-write-lines">
      {LINE_KEYS.slice(0, count).map((key) => (
        <span key={key} className="h-6 border-b border-border" />
      ))}
    </div>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="flex flex-col gap-2">
      {items.map((item) => (
        <li key={item} className="text-sm leading-relaxed">
          {item}
        </li>
      ))}
    </ul>
  );
}

export function WorkFitReportPrint({
  result,
  content,
  environmentNeeds,
  misfitSignals,
  preparedFor,
}: {
  result: FreeResult;
  content: WorkFitReportContent;
  environmentNeeds: WFDimensionDeepDive[];
  misfitSignals: WFMisfitSignal[];
  preparedFor: string | null;
}) {
  const { profileDeepDive } = content;
  const topLabels = result.topCategories.map((key) => WF_DIMENSION_LABELS[key] ?? key);

  return (
    <div className="print-document" data-testid="print-document-work_fit">
      <Cover
        eyebrow="Work Fit Assessment"
        assessmentLine="High Performer OS Work Fit"
        primaryType={result.primaryType}
        lead={topLabels.join(' · ')}
        preparedFor={preparedFor}
        completedAt={result.completedAt}
      />

      <PrintPage label="Your Profile" title="Profile Overview">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>What This Profile Needs</PrintLabel>
            <p className="text-sm leading-relaxed">{profileDeepDive.whatThisProfileNeeds}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>What Drains This Profile</PrintLabel>
            <p className="text-sm leading-relaxed">{profileDeepDive.whatDrainsThisProfile}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>How This Profile Evaluates Roles</PrintLabel>
            <p className="text-sm leading-relaxed">
              {profileDeepDive.howThisProfileEvaluatesRoles}
            </p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>What This Profile May Overlook</PrintLabel>
            <p className="text-sm leading-relaxed">{profileDeepDive.whatThisProfileMayOverlook}</p>
          </PrintCard>
        </div>
      </PrintPage>

      {/* Production gives each environment need its own page, titled with it. */}
      {environmentNeeds.map((need) => (
        <PrintPage
          key={need.dimension}
          label="Environment Need"
          title={need.label}
          testId="print-need-page"
        >
          <div className="flex flex-col gap-4">
            <p className="text-sm leading-relaxed">{need.whatThisMeans}</p>
            <PrintCard>
              <PrintLabel>Good Fit Looks Like</PrintLabel>
              <p className="text-sm leading-relaxed">{need.goodFitLooksLike}</p>
            </PrintCard>
            <PrintCard>
              <PrintLabel>Poor Fit Looks Like</PrintLabel>
              <p className="text-sm leading-relaxed">{need.poorFitLooksLike}</p>
            </PrintCard>
            <PrintCard>
              {/* Production's print wording; the screen report says "Questions to Ask". */}
              <PrintLabel>Questions to Ask in Interviews</PrintLabel>
              <Bullets items={need.questionsToAsk} />
            </PrintCard>
            <PrintCard>
              <PrintLabel>Red Flags to Watch For</PrintLabel>
              <Bullets items={need.redFlagsToWatchFor} />
            </PrintCard>
          </div>
        </PrintPage>
      ))}

      {misfitSignals.length > 0 ? (
        <PrintPage label="Your Data" title="Misfit Risk Signals">
          <div className="flex flex-col gap-4">
            {misfitSignals.map((dim) => (
              <PrintCard key={dim.dimension}>
                <PrintLabel>{dim.label}</PrintLabel>
                <p className="text-sm leading-relaxed">{dim.poorFitLooksLike}</p>
                <div className="mt-2">
                  <Bullets items={dim.redFlagsToWatchFor} />
                </div>
              </PrintCard>
            ))}
          </div>
        </PrintPage>
      ) : null}

      <PrintPage label="Hiring" title="Interview Question Bank">
        <div className="flex flex-col gap-3">
          {content.interviewQuestions.map((q) => (
            <PrintCard key={q.question}>
              <PrintLabel>{q.category}</PrintLabel>
              <p className="text-sm leading-relaxed">{q.question}</p>
            </PrintCard>
          ))}
        </div>
      </PrintPage>

      <PrintPage label="Hiring" title="Manager Fit Guide">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Supportive Manager Style</PrintLabel>
            <p className="text-sm leading-relaxed">{content.managerFitGuide.supportiveStyle}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>Manager Style That Creates Friction</PrintLabel>
            <p className="text-sm leading-relaxed">{content.managerFitGuide.frictionStyle}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>Questions to Ask the Hiring Manager</PrintLabel>
            <Bullets items={content.managerFitGuide.questionsToAsk} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Warning Signs</PrintLabel>
            <Bullets items={content.managerFitGuide.warningSigns} />
          </PrintCard>
        </div>
      </PrintPage>

      <PrintPage label="Hiring" title="Culture Risk Guide">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Culture Risks for Your Profile</PrintLabel>
            <Bullets items={content.cultureRiskGuide.cultureRisks} />
          </PrintCard>
          <PrintCard>
            {/* Production's print wording lowercases "for"; the screen does not. */}
            <PrintLabel>What to Listen for in Interviews</PrintLabel>
            <Bullets items={content.cultureRiskGuide.whatToListenFor} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Language That Can Hide Poor Fit</PrintLabel>
            <Bullets items={content.cultureRiskGuide.languageThatHidesPoorFit} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>How to Tell Values Are Real</PrintLabel>
            <p className="text-sm leading-relaxed">
              {content.cultureRiskGuide.howToTellValuesAreReal}
            </p>
          </PrintCard>
        </div>
      </PrintPage>

      <PrintPage label="Deciding" title="Offer Evaluation Checklist">
        <div className="flex flex-col gap-3">
          {content.offerChecklist.map((c) => (
            <PrintCard key={c.item}>
              <PrintLabel>{c.category}</PrintLabel>
              <p className="text-sm leading-relaxed">{c.item}</p>
            </PrintCard>
          ))}
        </div>
      </PrintPage>

      <PrintPage label="Your Role" title="Current Role Reflection">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Friction That May Be Environmental</PrintLabel>
            <Bullets items={content.currentRoleReflection.frictionMayBeEnvironmental} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>What to Clarify With Your Manager</PrintLabel>
            <Bullets items={content.currentRoleReflection.whatToClarifyWithManager} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>What to Ask for First</PrintLabel>
            <Bullets items={content.currentRoleReflection.whatToAskForFirst} />
          </PrintCard>
          <PrintCard testId="print-reflection-notes">
            <PrintLabel>Reflection Notes</PrintLabel>
            <WriteLines count={3} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Signs the Environment Will Not Change</PrintLabel>
            <Bullets items={content.currentRoleReflection.signsEnvironmentWontChange} />
          </PrintCard>
        </div>
      </PrintPage>

      <PrintPage label="Starting" title="30 / 60 / 90 Day Success Conditions">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>By Day 30</PrintLabel>
            <Bullets items={content.successConditions.thirtyDay} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>By Day 60</PrintLabel>
            <Bullets items={content.successConditions.sixtyDay} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>By Day 90</PrintLabel>
            <Bullets items={content.successConditions.ninetyDay} />
          </PrintCard>
        </div>
      </PrintPage>

      <PrintPage label="Deciding" title="Final Decision Guide">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Green Lights</PrintLabel>
            <Bullets items={content.finalDecisionGuide.greenLights} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Yellow Flags</PrintLabel>
            <Bullets items={content.finalDecisionGuide.yellowFlags} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Red Flags</PrintLabel>
            <Bullets items={content.finalDecisionGuide.redFlags} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Decision Prompts</PrintLabel>
            <Bullets items={content.finalDecisionGuide.decisionPrompts} />
          </PrintCard>
          <PrintCard testId="print-decision-notes">
            <PrintLabel>My Decision Notes</PrintLabel>
            <WriteLines count={3} />
          </PrintCard>
        </div>
      </PrintPage>

      {/* Production closes the Work Fit document with what to do next. */}
      <PrintPage
        label="Work Fit Report"
        title="Use this report."
        variant="final-page"
        testId="print-wf-final"
      >
        <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
          A Work Fit Report is only useful if it changes how you evaluate opportunities and make
          decisions. Bring it to interviews. Share the relevant sections with your manager. Use the
          questions.
        </p>

        <ol className="flex flex-col gap-3" data-testid="print-wf-steps">
          {WF_FINAL_STEPS.map((step, index) => (
            <li key={step.title} className="final-step flex gap-4">
              <span className="min-w-7 pt-px text-xs font-extrabold text-primary">
                {String(index + 1).padStart(2, '0')}
              </span>
              <p className="text-sm leading-relaxed">
                <strong>{step.title}</strong> {step.body}
              </p>
            </li>
          ))}
        </ol>

        <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
          High Performer OS. For personal development and career coaching purposes only.
        </p>
      </PrintPage>
    </div>
  );
}

/**
 * The Present Operator's trap, which production synthesises rather than scoring.
 * It is not one of the seven interpretation-trap questions, so it carries a
 * sentinel id and is always active where it applies.
 */
const PO_TRAP = {
  questionId: '-1',
  label: 'Action replacing useful preparation',
  description:
    'High scores here suggest that forward motion may be bypassing preparation, reflection, or strategic attention that would have improved the result. The issue is not action itself. The issue is action becoming automatic when the situation needed a deliberate pause first.',
  active: true,
};

/** Production's five closing steps on the Perspective print document. */
const FINAL_STEPS = [
  {
    title: 'Install your operating statement.',
    body: 'Place it somewhere visible and practice saying it before pressure arrives. The old lens is strong because it has been practiced for years. The replacement must be practiced just as consistently.',
  },
  {
    title: 'Start the 7-Day Reset Journal.',
    body: 'Use the daily tools to begin noticing and testing your dominant pattern in real situations. Open it from your report page or dashboard.',
  },
  {
    title: 'Complete the 30-Day New Mindset Practice.',
    body: 'Four weeks of structured practice: awareness, self-talk replacement, belief testing, and mental rehearsal. Start after the 7-Day Reset Journal.',
  },
  {
    title: 'Watch for the active interpretation traps.',
    body: 'The traps marked active in this report are the specific patterns most likely to interfere with your progress. Catching them early is what makes the difference.',
  },
  {
    title: 'Reassess in 30 days.',
    body: 'Return to the Perspective Assessment to measure how your pattern scores have shifted after the practice period.',
  },
];

export function PerspectiveReportPrint({
  result,
  content,
  interpretationTraps,
  preparedFor,
}: {
  result: FreeResult;
  content: PerspectiveReportContent;
  interpretationTraps: InterpretationTrap[];
  preparedFor: string | null;
}) {
  const slug = result.typeSlug ?? '';
  const secondaryKey = result.secondaryPatternKey ?? null;
  const secondaryLabel = secondaryKey
    ? (PERSPECTIVE_PATTERN_LABELS[secondaryKey] ?? secondaryKey)
    : null;
  const ranked = Object.entries(result.categoryScores)
    .map(([key, score]) => ({ key, label: PERSPECTIVE_PATTERN_LABELS[key] ?? key, score }))
    .sort((a, b) => b.score - a.score);

  /*
    Production's trap selection, copied from `perspective-report-print.tsx`.

    It does not print every trap: it picks the pattern's own primary trap, adds
    the secondary pattern's if that is a different one, and tops the selection
    up to three with any other active trap. The remaining active traps are
    listed separately under "Active Traps to Monitor".
  */
  const primaryTrapId = PSP_PRIMARY_TRAP_BY_SLUG[slug];
  // The trap table is keyed by slug; a secondary pattern arrives as a label, so
  // production converts before looking it up.
  const secondarySlug = secondaryLabel ? (PSP_LABEL_TO_SLUG[secondaryLabel] ?? '') : '';
  const secondaryTrapId = secondarySlug ? PSP_PRIMARY_TRAP_BY_SLUG[secondarySlug] : undefined;
  const selectedIds = new Set<number>();
  if (primaryTrapId) selectedIds.add(primaryTrapId);
  if (secondaryTrapId && secondaryTrapId !== primaryTrapId) selectedIds.add(secondaryTrapId);
  if (selectedIds.size < 3) {
    const extra = interpretationTraps.find(
      (t) => t.active && !selectedIds.has(Number(t.questionId)),
    );
    if (extra) selectedIds.add(Number(extra.questionId));
  }

  const rawSelected = interpretationTraps.filter((t) => selectedIds.has(Number(t.questionId)));

  /*
    The Present Operator's trap is not one of the scored seven: production
    synthesises it, and drops trap 2041 from the selection when it applies.
  */
  const selectedTraps =
    slug === 'present-operator'
      ? [PO_TRAP, ...rawSelected.filter((t) => Number(t.questionId) !== 2041)]
      : rawSelected;

  const hasTwoTraps = Boolean(
    secondaryTrapId && primaryTrapId && secondaryTrapId !== primaryTrapId,
  );

  const otherActiveTraps = interpretationTraps.filter(
    (t) => t.active && !selectedTraps.some((sel) => sel.questionId === t.questionId),
  );

  return (
    <div className="print-document" data-testid="print-document-perspective">
      <Cover
        eyebrow="Perspective Assessment"
        assessmentLine="Perspective — Cognitive Pattern Profile"
        primaryType={result.primaryType}
        lead={content.perspectiveSnapshot}
        preparedFor={preparedFor}
        completedAt={result.completedAt}
      />

      {ranked.length > 0 ? (
        <PrintPage label="Cognitive Pattern Profile" title="Pattern Score Profile">
          <div className="flex flex-col gap-2" data-testid="print-pattern-profile">
            {ranked.map((p) => (
              <div key={p.key} className="flex items-center justify-between gap-3 text-sm">
                <span>{p.label}</span>
                <span className="tabular-nums text-muted-foreground">{p.score.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {content.perspectiveSnapshot ? (
            <PrintCard className="mt-4" testId="print-snapshot">
              <PrintLabel>Your Pattern Snapshot</PrintLabel>
              <p className="text-sm leading-relaxed">{content.perspectiveSnapshot}</p>
            </PrintCard>
          ) : null}

          {/* Production shows this only when the top two scores are close. */}
          {secondaryLabel && result.isBlended ? (
            <PrintCard className="mt-4" testId="print-blended">
              <PrintLabel>Blended Pattern</PrintLabel>
              <p className="text-sm leading-relaxed">
                Your top two patterns are close in score, which means both are actively shaping your
                responses. Your dominant pattern leads, and your secondary pattern adds its own
                texture to how you think under pressure. Read both sections to understand the full
                picture.
              </p>
            </PrintCard>
          ) : null}
        </PrintPage>
      ) : null}

      <PrintPage label="Book Framework" title="Mindset Operating Pattern">
        {/* Production frames this page per pattern, under its own label. */}
        {PSP_MINDSET_FRAMING[slug] ? (
          <div className="mb-5">
            <PrintLabel>What This Means for Your Pattern</PrintLabel>
            <PrintCard>
              <p className="text-sm leading-relaxed" data-testid="print-mindset-framing">
                {PSP_MINDSET_FRAMING[slug]}
              </p>
            </PrintCard>
          </div>
        ) : null}
        <PrintCard>
          <PrintLabel>Focus and Attention Pattern</PrintLabel>
          <p className="text-sm leading-relaxed">{content.focusControlPattern}</p>
        </PrintCard>
      </PrintPage>

      <PrintPage label="Pattern Mechanics" title="How Your Mind Runs This Pattern">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Conscious and Subconscious Pattern</PrintLabel>
            <p className="text-sm leading-relaxed">{content.selfTalkPattern}</p>
            {PSP_CONSCIOUS_VS_SUBCONSCIOUS[slug] ? (
              <p
                className="mt-2 text-sm leading-relaxed text-muted-foreground"
                data-testid="print-conscious-note"
              >
                {PSP_CONSCIOUS_VS_SUBCONSCIOUS[slug]}
              </p>
            ) : null}
          </PrintCard>
          <PrintCard>
            <PrintLabel>Time Orientation</PrintLabel>
            <p className="text-sm leading-relaxed">{content.timeOrientationPattern}</p>
          </PrintCard>
          <PrintCard>
            <PrintLabel>Belief Friction</PrintLabel>
            <p className="text-sm leading-relaxed">{content.beliefFriction}</p>
          </PrintCard>
        </div>
      </PrintPage>

      {PS_HIDDEN_DEMAND[slug] ? (
        <PrintPage label="Hidden Demand" title="The Demand Behind the Pattern">
          <PrintCard testId="print-hidden-demand">
            <PrintLabel>Your Hidden Demand</PrintLabel>
            <p className="text-sm leading-relaxed">{PS_HIDDEN_DEMAND[slug]}</p>
          </PrintCard>

          <PrintCard className="mt-4" testId="print-demand-guidance">
            <PrintLabel>How to Work With This Demand</PrintLabel>
            <p className="mb-2 text-sm leading-relaxed">
              Identifying the demand is the first step. The ABCDE model (covered later in this
              report) gives you a structured method for testing whether the demand is actually true
              in any specific situation. The key question for any demand is: is this requirement
              absolutely true in every situation, with every person, under every condition? If the
              answer is no, the demand can be disputed.
            </p>
            <p className="text-sm leading-relaxed">
              Working with a demand does not mean ignoring it. It means testing it against the
              actual facts of the specific situation. A disputed demand that gets replaced with a
              more accurate expectation is what produces more useful action and less unnecessary
              friction.
            </p>
          </PrintCard>
        </PrintPage>
      ) : null}

      <PrintPage label="Pattern Impact" title="Where This Pattern Helps and Where It Costs">
        <div className="flex flex-col gap-4">
          <PrintCard>
            <PrintLabel>Where This Pattern Helps</PrintLabel>
            <Bullets items={content.patternStrengths} />
          </PrintCard>
          <PrintCard>
            <PrintLabel>Where This Pattern Costs You</PrintLabel>
            <Bullets items={content.costWhenOverused} />
          </PrintCard>
        </div>
      </PrintPage>

      {secondaryLabel ? (
        <PrintPage label="Secondary Pattern" title="Secondary Pattern and Combined Effect">
          <PrintCard>
            <PrintLabel>Secondary Pattern</PrintLabel>
            <p className="text-sm font-semibold">{secondaryLabel}</p>
            {secondaryKey && PSP_SECONDARY_AS_MODIFIER[secondaryKey] ? (
              <p
                className="mt-2 text-sm leading-relaxed text-muted-foreground"
                data-testid="print-secondary-modifier"
              >
                {PSP_SECONDARY_AS_MODIFIER[secondaryKey]}
              </p>
            ) : null}
          </PrintCard>
        </PrintPage>
      ) : null}

      <PrintPage label="Traps and Reframes" title="Interpretation Traps and Reframe">
        <p className="mb-3 text-sm leading-relaxed" data-testid="print-trap-intro">
          {PSP_TRAP_PAGE_INTRO[slug] ??
            'Interpretation traps are the specific places where this cognitive pattern most reliably converts neutral or ambiguous information into something that costs you.'}
        </p>
        {PSP_DOMINANT_INTERACTION_NOTE[slug] ? (
          <p
            className="mb-4 text-sm leading-relaxed text-muted-foreground"
            data-testid="print-trap-interaction"
          >
            {PSP_DOMINANT_INTERACTION_NOTE[slug]}
          </p>
        ) : null}
        <PrintLabel>{hasTwoTraps ? 'Your Pattern Traps' : 'Your Primary Trap'}</PrintLabel>
        <div className="flex flex-col gap-3" data-testid="print-selected-traps">
          {selectedTraps.map((trap) => (
            <PrintCard key={trap.questionId} testId="print-trap">
              <PrintLabel>{trap.label}</PrintLabel>
              <p className="text-sm leading-relaxed">{trap.description}</p>
            </PrintCard>
          ))}
        </div>

        {otherActiveTraps.length > 0 ? (
          <div className="mt-4" data-testid="print-other-traps">
            <PrintLabel>Active Traps to Monitor</PrintLabel>
            <ul className="flex flex-col gap-2">
              {otherActiveTraps.map((trap) => (
                <li key={trap.questionId} className="text-sm leading-relaxed">
                  <strong>{trap.label}.</strong> {trap.description}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <PrintCard className="mt-4">
          <PrintLabel>Immediate Reframe</PrintLabel>
          <p className="text-sm leading-relaxed">{content.immediateReframe}</p>
        </PrintCard>
      </PrintPage>

      {PS_OPERATING_STATEMENTS[slug] || PS_OLD_LENS[slug] ? (
        <PrintPage label="Reframe and Operating Statement" title="Your Operating Statement">
          <div className="flex flex-col gap-4">
            {PS_OLD_LENS[slug] ? (
              <PrintCard testId="print-old-lens">
                <PrintLabel>Old Lens</PrintLabel>
                <p className="text-sm italic leading-relaxed">
                  &ldquo;{PS_OLD_LENS[slug]}&rdquo;
                </p>
              </PrintCard>
            ) : null}
            {PS_OPERATING_STATEMENTS[slug] ? (
              <PrintCard testId="print-operating-statement">
                <PrintLabel>Your Operating Statement</PrintLabel>
                <p className="text-base font-bold">
                  &ldquo;{PS_OPERATING_STATEMENTS[slug]}&rdquo;
                </p>
              </PrintCard>
            ) : null}
            {PS_WATCH_FOR_NOTES[slug] ? (
              <PrintCard>
                <PrintLabel>What to Watch For</PrintLabel>
                <p className="text-sm leading-relaxed">{PS_WATCH_FOR_NOTES[slug]}</p>
              </PrintCard>
            ) : null}
          </div>
        </PrintPage>
      ) : null}

      <PrintPage label="Belief Testing" title="ABCDE: Testing a Belief in Practice">
        <div className="flex flex-col gap-3">
          {(
            [
              ['A — Activating Event', content.abcdeExample.activatingEvent],
              ['B — Belief', content.abcdeExample.belief],
              ['C — Consequence', content.abcdeExample.consequence],
              ['D — Disputation', content.abcdeExample.disputation],
              ['E — Effective New Belief', content.abcdeExample.newEffect],
            ] as const
          )
            .filter(([, body]) => body)
            .map(([label, body]) => (
              <PrintCard key={label}>
                <PrintLabel>{label}</PrintLabel>
                <p className="text-sm leading-relaxed">{body}</p>
              </PrintCard>
            ))}
        </div>
        <div className="mt-5 rounded-lg border border-border p-4" data-testid="print-abcde-usage">
          <PrintLabel>How to Use This in Real Situations</PrintLabel>
          <p className="text-sm leading-relaxed">
            When your dominant pattern activates in an actual situation, write out the five steps
            using that situation. The act of writing separates what actually happened from what your
            mind added to it. That separation is what makes the dispute possible. The dispute is what
            makes the new belief credible. The credible belief is what produces the more useful
            action. Day 5 of the 7-Day Reset Workbook provides a guided ABCDE worksheet for a real
            situation from your week.
          </p>
        </div>
      </PrintPage>

      <PrintPage
        label="From Insight to Practice"
        title="From Insight to Practice"
        variant="final-page"
        testId="print-final-page"
      >
        <div className="flex flex-col gap-4">
          <p className="text-lg font-bold" data-testid="print-final-title">
            What Comes Next
          </p>

          <PrintCard>
            <PrintLabel>Step 1: 7-Day Perspective Reset Workbook</PrintLabel>
            <p className="text-sm leading-relaxed" data-testid="print-seven-day-narrative">
              {PSP_SEVEN_DAY_NARRATIVE[slug] ??
                'A structured daily practice for interrupting your dominant pattern in real time.'}
            </p>
          </PrintCard>

          <PrintCard>
            <PrintLabel>Step 2: 30-Day New Mindset Practice Workbook</PrintLabel>
            <p className="mb-2 text-sm leading-relaxed">
              Four weeks of structured practice: awareness, self-talk replacement, belief testing,
              and mental rehearsal. Start after the 7-Day Reset Journal.
            </p>
            {/* Production outlines the four weeks for this pattern by name. */}
            {(PSP_THIRTY_DAY_WEEKS[slug] ?? []).length > 0 ? (
              <ul className="flex flex-col gap-2" data-testid="print-thirty-day-weeks">
                {(PSP_THIRTY_DAY_WEEKS[slug] ?? []).map((week, index) => (
                  <li key={week.theme} className="text-sm leading-relaxed">
                    <strong>
                      Week {index + 1}: {week.theme}.
                    </strong>{' '}
                    {week.purpose}
                  </li>
                ))}
              </ul>
            ) : null}
          </PrintCard>

          {/* Production's five closing steps, numbered. */}
          <ol className="flex flex-col gap-3" data-testid="print-final-steps">
            {FINAL_STEPS.map((step, index) => (
              <li key={step.title} className="final-step flex gap-4">
                <span className="min-w-7 pt-px text-xs font-extrabold text-primary">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <p className="text-sm leading-relaxed">
                  <strong>{step.title}</strong> {step.body}
                </p>
              </li>
            ))}
          </ol>

          <p className="text-xs leading-relaxed text-muted-foreground">
            High Performer OS is for personal development and coaching purposes only. It is not
            medical, psychological, or clinical advice and is not a diagnostic instrument.
          </p>
        </div>
      </PrintPage>
    </div>
  );
}
